/**
 * 分拣过程动画 —— 帧播放器
 *
 * 架构②：播放器自管时间线（provider + 当前帧 t），GSAP 只负责「两帧之间的 FLIP 过渡」。
 * seek 本质 = 渲染第 t 帧，天然确定性：渲染结果仅由 provider.frameAt(t) 决定，与播放历史无关。
 *
 * 帧来源经 `FrameProvider` 抽象（reconstruction：`Reconstructor` 懒重建 ProcessFrame[]；
 * 回退/测试：`ArrayFrameProvider` 包一份已物化 Frame[]）。`load(Frame[])` 保留向后兼容
 * （内部包成 ArrayFrameProvider），`loadProvider` 接任意 provider。
 *
 * - play/pause/next/prev/seek/setSpeed/nextKeyFrame/prevKeyFrame 仅改变 t 与播放态，并通知订阅者重渲。
 * - 节奏由 dwellMs 驱动：自动播放每帧停留 = frame.dwellMs / speed；每帧重调度。
 * - 关键帧跳转：经 provider.actionAt(i) + 关键帧动作表扫描，不强制物化帧（适配 Lazy 重建）。
 * - 边界：首帧后退无效；末帧前进无效；自动播放到末帧停止并切暂停。
 * - 校验：load(Frame[]) 时用 isValidFrames；非法置 invalid，不进入播放。
 */

import type { Frame, FrameProvider, StepAction } from './types';
import { ArrayFrameProvider } from './reconstructor';

/** 关键帧动作（contract.md §5），与 reconstructor 一致 */
const KEY_FRAME_ACTIONS = new Set<StepAction>([
  'init', 'seal-pallet', 'oversized', 'merge-pick', 'merge-round-end', 'final',
]);

function isKeyFrameAction(a: StepAction | undefined): boolean {
  return !!a && KEY_FRAME_ACTIONS.has(a);
}

/** frame.dwellMs 缺省时的回退停留时长（1x） */
const DEFAULT_DWELL_MS = 1000;

export interface PlayerSnapshot {
  /** 帧总数 */
  total: number;
  /** 当前帧下标 */
  t: number;
  /** 是否播放中 */
  playing: boolean;
  /** 倍速 */
  speed: number;
  /** 过程数据是否合法 */
  valid: boolean;
  /** 是否在首帧 */
  atStart: boolean;
  /** 是否在末帧 */
  atEnd: boolean;
}

export type PlayerListener = (snapshot: PlayerSnapshot) => void;

/** 校验帧序列合法性：非空且每帧含 phase/action/items/pallets/caption/metrics */
export function isValidFrames(frames: unknown): frames is Frame[] {
  if (!Array.isArray(frames) || frames.length === 0) return false;
  return frames.every(f =>
    f && typeof f === 'object'
    && 'phase' in f && 'action' in f && 'items' in f && 'pallets' in f
    && 'caption' in f && 'metrics' in f
    && Array.isArray((f as Frame).items)
    && Array.isArray((f as Frame).pallets)
  );
}

export class FramePlayer {
  private provider: FrameProvider | null = null;
  private t = 0;
  private playing = false;
  private speed = 1;
  private valid = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private listeners = new Set<PlayerListener>();

  /** 当前帧（无数据时 undefined） */
  current(): Frame | undefined {
    return this.valid ? this.provider?.frameAt(this.t) : undefined;
  }

  /** 取指定帧 */
  frameAt(index: number): Frame | undefined {
    return this.provider?.frameAt(this.clamp(index));
  }

  /** 加载已物化的 Frame[]（向后兼容；内部包成 ArrayFrameProvider）；非法置 invalid。返回是否合法 */
  load(frames: unknown): boolean {
    this.pause();
    const ok = isValidFrames(frames);
    this.provider = ok ? new ArrayFrameProvider(frames as Frame[]) : null;
    this.valid = ok;
    this.t = 0;
    this.emit();
    return ok;
  }

  /** 加载帧提供者（Reconstructor 等）；provider.length===0 视为非法 */
  loadProvider(provider: FrameProvider): boolean {
    this.pause();
    this.provider = provider;
    this.valid = provider.length > 0;
    this.t = 0;
    this.emit();
    return this.valid;
  }

  /** 订阅状态变化，返回取消订阅函数 */
  subscribe(listener: PlayerListener): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => this.listeners.delete(listener);
  }

  snapshot(): PlayerSnapshot {
    const total = this.provider?.length ?? 0;
    return {
      total,
      t: this.t,
      playing: this.playing,
      speed: this.speed,
      valid: this.valid,
      atStart: this.t <= 0,
      atEnd: this.t >= total - 1,
    };
  }

  play(): void {
    if (!this.valid || (this.provider?.length ?? 0) === 0) return;
    if (this.playing) return;
    if (this.t >= (this.provider!.length - 1)) return; // 已到末帧，不自动推进
    this.playing = true;
    this.emit();
    this.scheduleTick();
  }

  pause(): void {
    if (!this.playing) return;
    this.playing = false;
    this.clearTimer();
    this.emit();
  }

  toggle(): void {
    this.playing ? this.pause() : this.play();
  }

  next(): void {
    this.seek(this.t + 1);
  }

  prev(): void {
    this.seek(this.t - 1);
  }

  /** 下一关键帧下标（其后第一个 keyFrame）；无则 null */
  nextKeyFrameIndex(): number | null {
    const n = this.provider?.length ?? 0;
    for (let i = this.t + 1; i < n; i++) {
      if (isKeyFrameAction(this.provider!.actionAt(i))) return i;
    }
    return null;
  }

  /** 上一关键帧下标（其前最后一个 keyFrame）；无则 null */
  prevKeyFrameIndex(): number | null {
    for (let i = this.t - 1; i >= 0; i--) {
      if (isKeyFrameAction(this.provider!.actionAt(i))) return i;
    }
    return null;
  }

  /** 跳到下一关键帧（经 seek 打断过渡）；无则停在当前帧 */
  nextKeyFrame(): void {
    const i = this.nextKeyFrameIndex();
    if (i !== null) this.seek(i);
  }

  /** 跳到上一关键帧（经 seek 打断过渡）；无则停在当前帧 */
  prevKeyFrame(): void {
    const i = this.prevKeyFrameIndex();
    if (i !== null) this.seek(i);
  }

  /** 定位到指定帧（越界自动钳制），并通知重渲 */
  seek(index: number): void {
    if (!this.valid) return;
    this.t = this.clamp(index);
    // seek 时停止自动播放（用户主动定位）
    if (this.playing) {
      this.playing = false;
      this.clearTimer();
    }
    this.emit();
  }

  setSpeed(speed: number): void {
    if (speed <= 0) return;
    this.speed = speed;
    if (this.playing) {
      this.clearTimer();
      this.scheduleTick();
    }
    this.emit();
  }

  /** 释放资源 */
  dispose(): void {
    this.clearTimer();
    this.listeners.clear();
  }

  private clamp(index: number): number {
    const last = (this.provider?.length ?? 1) - 1;
    if (last < 0) return 0;
    return Math.max(0, Math.min(last, index));
  }

  /** 按当前帧 dwellMs/speed 调度下一次推进 */
  private scheduleTick(): void {
    this.clearTimer();
    const dwell = (this.provider?.frameAt(this.t)?.dwellMs ?? DEFAULT_DWELL_MS) / this.speed;
    this.timer = setTimeout(this.tick, dwell);
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private tick = (): void => {
    if (!this.playing) return;
    const last = (this.provider?.length ?? 1) - 1;
    // 推进一帧；到达末帧立即停止并切暂停
    this.t = Math.min(this.t + 1, last);
    if (this.t >= last) {
      this.playing = false;
      this.clearTimer();
      this.emit();
      return;
    }
    this.emit();
    this.scheduleTick();
  };

  private emit(): void {
    const snap = this.snapshot();
    this.listeners.forEach(fn => fn(snap));
  }
}
